import React, { useState, useEffect, useCallback, forwardRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box, Card, CardContent, Typography, Avatar, Chip, Switch,
    Snackbar, Alert, Slide, IconButton, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip,
} from "@mui/material";
import ArrowBackRoundedIcon      from "@mui/icons-material/ArrowBackRounded";
import PhoneRoundedIcon          from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon          from "@mui/icons-material/EmailOutlined";
import BadgeRoundedIcon          from "@mui/icons-material/BadgeRounded";
import FingerprintRoundedIcon    from "@mui/icons-material/FingerprintRounded";
import ArticleRoundedIcon        from "@mui/icons-material/ArticleRounded";
import DirectionsCarRoundedIcon  from "@mui/icons-material/DirectionsCarRounded";
import CalendarMonthRoundedIcon  from "@mui/icons-material/CalendarMonthRounded";
import OpenInNewRoundedIcon      from "@mui/icons-material/OpenInNewRounded";
import CheckCircleRoundedIcon    from "@mui/icons-material/CheckCircleRounded";
import PersonRoundedIcon         from "@mui/icons-material/PersonRounded";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";
import { decryptId } from "../helper/idCodec.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B", surface: "#FFFFFF",
};

const AVATAR_PALETTE = [
    [T.tealLt, T.teal], [T.orangeLt, T.orange], [T.indigoLt, T.indigo],
    [T.emeraldLt, T.emerald], [T.amberLt, T.amber], [T.roseLt, T.rose],
];
const avatarBg = (name = "") => AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];
const initials = (name = "") => { const p = name.trim().split(" "); return p.length >= 2 ? (p[0][0]+p[1][0]).toUpperCase() : (p[0]?.[0] ?? "?").toUpperCase(); };
const fmtDate  = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

const SlideTransition = forwardRef((props, ref) => <Slide direction="down" ref={ref} {...props} />);

// ── Section card ──────────────────────────────────────────────────────────
const SCard = ({ children, sx = {} }) => (
    <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", ...sx }}>
        {children}
    </Card>
);

const SHead = ({ icon, title }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: T.tealLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {React.cloneElement(icon, { sx: { fontSize: 15, color: T.teal } })}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</Typography>
    </Box>
);

const InfoRow = ({ icon, label, value, action, accent }) => (
    <Box sx={{ display: "flex", alignItems: "center", py: 1.2, borderBottom: `1px solid ${T.border}`, "&:last-child": { borderBottom: "none" }, gap: 1.5 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: "7px", bgcolor: T.bg, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {React.cloneElement(icon, { sx: { fontSize: 14, color: T.muted } })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.muted, textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: accent || T.text, mt: 0.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {value || "—"}
            </Typography>
        </Box>
        {action}
    </Box>
);

// ── Document preview button ───────────────────────────────────────────────
const DocBtn = ({ url, label, onOpen }) => url ? (
    <Tooltip title={`View ${label}`}>
        <Box onClick={() => onOpen(label, url)} sx={{
            display: "inline-flex", alignItems: "center", gap: 0.4,
            px: 1.2, py: 0.3, bgcolor: T.tealLt, borderRadius: "7px",
            cursor: "pointer", color: T.teal, fontSize: 11, fontWeight: 700,
            border: `1px solid ${T.teal}22`, "&:hover": { bgcolor: T.teal, color: "#fff" }, transition: "all 0.14s",
        }}>
            <OpenInNewRoundedIcon sx={{ fontSize: 12 }} /> View
        </Box>
    </Tooltip>
) : (
    <Typography sx={{ fontSize: 11, color: T.muted }}>Not uploaded</Typography>
);

// ── Main component ────────────────────────────────────────────────────────
export default function DriverProfile() {
    const { id }    = useParams();
    const driverId  = id ? decryptId(id) : "";
    const navigate  = useNavigate();

    const [driver,   setDriver]   = useState(null);
    const [loading,  setLoading]  = useState(true);
    const [toggling, setToggling] = useState(false);
    const [snack,    setSnack]    = useState({ open: false, msg: "", sev: "success" });
    const [docView,  setDocView]  = useState({ open: false, label: "", url: "" });

    const toast = (msg, sev = "success") => setSnack({ open: true, msg, sev });

    const fetchDriver = useCallback(async () => {
        if (!driverId) { setLoading(false); toast("Driver ID not found.", "error"); return; }
        setLoading(true);
        try {
            const url = API_ENDPOINTS.fetchDriverById.replace(":driver_id", driverId);
            const res = await getData(url);
            if (res.statusCode !== 200) throw new Error(res.message || "Failed to retrieve driver.");
            const d = res.result;
            setDriver({
                name:         `${d.first_name} ${d.last_name}`.trim(),
                driverId:     d.driver_id,
                onDuty:       d.isOnDuty,
                status:       d.status,
                mobile:       d.mobile_num,
                email:        d.email,
                licenseNo:    d.license_no,
                aadharNo:     d.aadhar_no,
                licenseFile:  d.licence_file,
                aadharFile:   d.aadhar_file,
                pic:          d.pic,
                createdAt:    d.createdAt,
                updatedAt:    d.updatedAt,
            });
        } catch (e) { toast(e.message || "Failed to fetch driver.", "error"); }
        finally     { setLoading(false); }
    }, [driverId]);

    useEffect(() => { fetchDriver(); }, [fetchDriver]);

    const handleDutyToggle = async () => {
        if (!driver || toggling) return;
        const next = !driver.onDuty;
        setToggling(true);
        setDriver(p => ({ ...p, onDuty: next }));
        try {
            const res = await postData(API_ENDPOINTS.markOnDuty, { driver_id: driver.driverId, isOnDuty: next });
            if (res.statusCode === 200) toast(`Driver marked ${next ? "On Duty" : "Off Duty"}.`);
            else { setDriver(p => ({ ...p, onDuty: !next })); throw new Error(res.message); }
        } catch (e) {
            setDriver(p => ({ ...p, onDuty: !next }));
            toast(e.message || "Failed to update duty status.", "error");
        } finally { setToggling(false); }
    };

    if (loading) return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", p: 3 }}>
            <Skeleton variant="rounded" height={160} sx={{ borderRadius: "20px", mb: 3 }} />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2 }}>
                {[1,2,3].map(i => <Skeleton key={i} variant="rounded" height={180} sx={{ borderRadius: "16px" }} />)}
            </Box>
        </Box>
    );

    if (!driver) return <Alert severity="error" sx={{ m: 3 }}>Driver not found.</Alert>;

    const [bg, fg] = avatarBg(driver.name);
    const isOnDuty = driver.onDuty;

    // document viewer ext detection
    const docViewExt  = (docView.url || "").split("?")[0].split(".").pop().toLowerCase();
    const docIsImg    = ["jpg","jpeg","png","gif","webp","svg"].includes(docViewExt);
    const docViewerSrc= docIsImg ? docView.url : `https://docs.google.com/viewer?url=${encodeURIComponent(docView.url)}&embedded=true`;

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                    <IconButton onClick={() => navigate("/app/drivers")}
                        sx={{ bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "10px", color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
                        <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>Drivers</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, flexWrap: "wrap" }}>
                    {/* Avatar */}
                    <Avatar src={driver.pic} sx={{ width: 68, height: 68, bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 22, fontWeight: 800, border: "2px solid rgba(255,255,255,0.35)", flexShrink: 0 }}>
                        {!driver.pic && initials(driver.name)}
                    </Avatar>

                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 0.5 }}>
                            <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                {driver.name}
                            </Typography>
                            <Chip label={isOnDuty ? "On Duty" : "Off Duty"} size="small" sx={{
                                height: 22, fontSize: 11, fontWeight: 700, borderRadius: "7px",
                                bgcolor: isOnDuty ? T.emeraldLt : T.roseLt,
                                color:   isOnDuty ? T.emerald  : T.rose,
                            }} />
                            {driver.status && (
                                <Chip label={driver.status} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, borderRadius: "7px", bgcolor: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", textTransform: "capitalize" }} />
                            )}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", px: 1.2, py: 0.3 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>Driver #{driver.driverId}</Typography>
                            </Box>
                            {driver.mobile && <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>{driver.mobile}</Typography>}
                            {driver.createdAt && <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>Joined {fmtDate(driver.createdAt)}</Typography>}
                        </Box>
                    </Box>

                    {/* Duty toggle — right side */}
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, bgcolor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "14px", px: 2, py: 1.5, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: "0.4px" }}>Duty</Typography>
                        <Switch
                            checked={isOnDuty}
                            onChange={handleDutyToggle}
                            disabled={toggling}
                            sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: T.emerald },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: T.emerald },
                                "& .MuiSwitch-track": { bgcolor: T.roseLt },
                            }}
                        />
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: isOnDuty ? T.emerald : T.rose }}>
                            {isOnDuty ? "On" : "Off"}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* ── Info grid ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 2 }}>

                {/* Contact */}
                <SCard>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <SHead icon={<PersonRoundedIcon />} title="Contact" />
                        <InfoRow icon={<PhoneRoundedIcon />}  label="Mobile Number" value={driver.mobile} />
                        <InfoRow icon={<EmailRoundedIcon />}   label="Email Address"  value={driver.email || "Not provided"} />
                    </CardContent>
                </SCard>

                {/* Identity */}
                <SCard>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <SHead icon={<BadgeRoundedIcon />} title="Identity" />
                        <InfoRow icon={<BadgeRoundedIcon />}       label="License Number" value={driver.licenseNo}
                            action={<DocBtn url={driver.licenseFile} label="License" onOpen={(l, u) => setDocView({ open: true, label: l, url: u })} />} />
                        <InfoRow icon={<FingerprintRoundedIcon />}  label="Aadhaar Number" value={driver.aadharNo}
                            action={<DocBtn url={driver.aadharFile} label="Aadhaar" onOpen={(l, u) => setDocView({ open: true, label: l, url: u })} />} />
                    </CardContent>
                </SCard>

                {/* Status + timeline */}
                <SCard>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <SHead icon={<DirectionsCarRoundedIcon />} title="Status & Timeline" />
                        <InfoRow icon={<DirectionsCarRoundedIcon />} label="Duty Status"  value={isOnDuty ? "On Duty" : "Off Duty"} accent={isOnDuty ? T.emerald : T.rose} />
                        <InfoRow icon={<CheckCircleRoundedIcon />}   label="Account Status" value={driver.status || "—"} />
                        <InfoRow icon={<CalendarMonthRoundedIcon />} label="Joined"       value={fmtDate(driver.createdAt)} />
                        <InfoRow icon={<CalendarMonthRoundedIcon />} label="Last Updated" value={fmtDate(driver.updatedAt)} />
                    </CardContent>
                </SCard>
            </Box>

            {/* ── Document viewer dialog ── */}
            <Dialog open={docView.open} onClose={() => setDocView({ open: false, label: "", url: "" })}
                maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: "20px", height: "90vh" } }}>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 800, fontSize: 16, fontFamily: "'Plus Jakarta Sans', sans-serif", borderBottom: `1px solid ${T.border}`, pb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: T.tealLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <ArticleRoundedIcon sx={{ fontSize: 15, color: T.teal }} />
                        </Box>
                        {docView.label}
                    </Box>
                    <Tooltip title="Open in new tab">
                        <IconButton size="small" component="a" href={docView.url} target="_blank" rel="noopener noreferrer"
                            sx={{ color: T.teal, bgcolor: T.tealLt, borderRadius: "8px" }}>
                            <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#1a1a2e" }}>
                    {docView.url && (docIsImg
                        ? <Box component="img" src={docView.url} alt={docView.label} sx={{ maxWidth: "100%", maxHeight: "calc(90vh - 120px)", objectFit: "contain" }} />
                        : <iframe key={docView.url} src={docViewerSrc} title={docView.label} style={{ width: "100%", height: "calc(90vh - 120px)", border: "none" }} />
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${T.border}`, px: 3, py: 1.5, gap: 1 }}>
                    <Button onClick={() => setDocView({ open: false, label: "", url: "" })} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>Close</Button>
                    <Button variant="contained" disableElevation component="a" href={docView.url} target="_blank" rel="noopener noreferrer"
                        startIcon={<OpenInNewRoundedIcon />}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: T.teal, "&:hover": { bgcolor: T.tealDk } }}>
                        Open in new tab
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "center" }} TransitionComponent={SlideTransition}>
                <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.sev} variant="filled" sx={{ borderRadius: "12px" }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
